import { getUrlFromEnv, ScheduleItemV2 } from '@hopin-team/api-hopin';

type CalendarEventDetails = {
  description: string | null;
  externalId: string | undefined;
  redeemCode?: string | null;
  room: ScheduleItemV2['room'];
};

export function getCalendarDetails(details: CalendarEventDetails) {
  return {
    calLocation: getCalLocation(details),
    calDescription: getCalDescription(details),
    calUrl: getCalUrl(details),
  };
}

/**
 * Returns calendar's location
 *
 * For pure onsite segment, location is a room's name.
 * Otherwise its an URL.
 */
function getCalLocation(details: CalendarEventDetails) {
  const { room } = details;

  if (room !== null) {
    return room.name;
  }

  return getLocationURL(details);
}

/**
 * Returns calendar's description
 *
 * For pure onsite segment, returns segment's description.
 * Otherwise segment's description plus location.
 */
function getCalDescription(details: CalendarEventDetails) {
  const { room, description } = details;

  if (room !== null) {
    return description || '';
  }

  return [description, getLocationURL(details)].filter(Boolean).join('\n');
}

/**
 * Returns calendar's URL when event is not onsite
 */
function getCalUrl(details: CalendarEventDetails) {
  return details.room !== null ? getLocationURL(details) : '';
}

/**
 * Returns event's location URL
 *
 * For users that have been invited through magic link (incluiding Speakers),
 * we attach magic link at the end of description
 *
 * Otherwise we provide /cal/:external_id link which redirects to schedule item's target activity or location
 */
function getLocationURL(details: CalendarEventDetails) {
  const { redeemCode, externalId } = details;

  if (redeemCode) {
    return buildUrlWithParams(
      getUrlFromEnv(`magic_links/${redeemCode}/accept`),
      {
        cal: externalId,
      },
    );
  }

  return getUrlFromEnv(`cal/${externalId}`);
}

const buildUrlWithParams = (base: string, params: { [key: string]: any }) => {
  const url = new URL(base);
  Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
  return url.toString();
};
